import type { ServerFrame } from '@liniya/api-client';
import type {
  IceCandidateEvent,
  IceCandidateInit,
  MediaStreamLike,
  PeerConnectionLike,
  TrackEvent,
} from './webrtc';
import { newUuid } from '@liniya/api-client';
import { useCallback, useEffect, useRef } from 'react';
import { create } from 'zustand';

import { api } from '../api';
import { notify } from '../store/ui';
import { onAppClosing, webRtc } from '../platform';
import { useRealtime } from '../realtime/provider';

export type CallPhase = 'idle' | 'ringing' | 'incoming' | 'connecting' | 'active' | 'ended';

interface CallState {
  phase: CallPhase;
  callId: string | null;
  chatId: string | null;
  peerUserId: string | null;
  peerName: string;
  mediaKind: 'audio' | 'video';
  micEnabled: boolean;
  cameraEnabled: boolean;
  startedAt: number | null;
  /** Held outside React because a MediaStream is not serialisable state. */
  localStream: MediaStreamLike | null;
  remoteStream: MediaStreamLike | null;

  set: (patch: Partial<CallState>) => void;
  reset: () => void;
}

const initial = {
  phase: 'idle' as CallPhase,
  callId: null,
  chatId: null,
  peerUserId: null,
  peerName: '',
  mediaKind: 'audio' as const,
  micEnabled: true,
  cameraEnabled: true,
  startedAt: null,
  localStream: null,
  remoteStream: null,
};

export const useCallStore = create<CallState>((set) => ({
  ...initial,
  set: (patch) => set(patch),
  reset: () => set({ ...initial }),
}));

export interface CallSession {
  start: (input: {
    chatId: string;
    calleeUserId: string;
    mediaKind: 'audio' | 'video';
    peerName: string;
  }) => Promise<void>;
  accept: () => void;
  reject: () => void;
  hangUp: () => void;
  toggleMicrophone: () => void;
  toggleCamera: () => void;
}

/**
 * One 1:1 call at a time.
 *
 * The three concerns stay separate on purpose: signalling travels over the
 * application's WebSocket, the media path is a peer connection the server
 * never sees, and the interface reads phase from the store. Mixing them is
 * what turns call code into something nobody can safely change.
 *
 * ICE servers are fetched per call rather than cached, because relay
 * credentials are deliberately short-lived and the deployment — not this
 * client — decides which servers exist.
 */
export function useCallSessionInstance(): CallSession {
  const { client, subscribe } = useRealtime();
  const peer = useRef<PeerConnectionLike | null>(null);
  const pendingCandidates = useRef<IceCandidateInit[]>([]);
  const store = useCallStore;

  const teardown = useCallback(() => {
    peer.current?.close();
    peer.current = null;
    pendingCandidates.current = [];
    const { localStream } = store.getState();
    localStream?.getTracks().forEach((track) => track.stop());
    store.getState().reset();
  }, [store]);

  const createPeer = useCallback(
    async (callId: string, mediaKind: 'audio' | 'video') => {
      // One call, one connection and one microphone. A second pass through
      // here — a double tap on "Ответить" is enough — would otherwise leave
      // the first connection open and still playing, so both copies of the
      // other side's voice reach the speaker and the microphone feeds them
      // back. Whatever is already running is closed before anything new starts.
      peer.current?.close();
      peer.current = null;
      store
        .getState()
        .localStream?.getTracks()
        .forEach((track) => track.stop());
      store.getState().set({ localStream: null, remoteStream: null });

      const configuration = await api.calls.iceConfiguration();
      const connection = webRtc().createPeerConnection({
        iceServers: configuration.ice_servers as unknown[],
      });

      const localStream = await webRtc().getUserMedia({
        // Asked for explicitly rather than left to the default. On a phone the
        // earpiece is centimetres from the microphone, so without cancellation
        // the caller hears themselves back and the loop builds until it howls.
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
        video: mediaKind === 'video',
      });
      localStream.getTracks().forEach((track) => connection.addTrack(track, localStream));

      connection.addEventListener('track', (event: TrackEvent) => {
        // Audio and video arrive as separate track events carrying the same
        // stream. Assigning unconditionally would clear a working stream on
        // the rare event that comes without one.
        const stream = event.streams[0];
        if (stream) store.getState().set({ remoteStream: stream });
      });

      connection.addEventListener('icecandidate', (event: IceCandidateEvent) => {
        if (!event.candidate) return;
        client?.trySend('call.ice_candidate', {
          call_id: callId,
          candidate: event.candidate.candidate,
          sdp_mid: event.candidate.sdpMid,
          sdp_mline_index: event.candidate.sdpMLineIndex,
        });
      });

      connection.addEventListener('connectionstatechange', () => {
        const state = connection.connectionState;
        if (state === 'connected') {
          store.getState().set({ phase: 'active', startedAt: Date.now() });
        } else if (state === 'failed' || state === 'closed') {
          // A failed connection usually means no route was found, even through
          // the relay; there is nothing to wait for.
          notify('Соединение прервано.', 'error');
          teardown();
        }
      });

      peer.current = connection;
      store.getState().set({ localStream, mediaKind });
      return connection;
    },
    [client, store, teardown],
  );

  const start = useCallback(
    async (input: {
      chatId: string;
      calleeUserId: string;
      mediaKind: 'audio' | 'video';
      peerName: string;
    }) => {
      if (store.getState().phase !== 'idle') {
        notify('Уже идёт другой звонок.', 'error');
        return;
      }
      if (!client?.isReady()) {
        notify('Нет соединения с сервером. Звонок недоступен.', 'error');
        return;
      }
      // Not `crypto.randomUUID`: Hermes has no Web Crypto.
      const callId = newUuid();
      store.getState().set({
        phase: 'ringing',
        callId,
        chatId: input.chatId,
        peerUserId: input.calleeUserId,
        peerName: input.peerName,
        mediaKind: input.mediaKind,
      });
      try {
        const connection = await createPeer(callId, input.mediaKind);
        const offer = await connection.createOffer();
        await connection.setLocalDescription(offer);
        client.trySend('call.offer', {
          call_id: callId,
          chat_id: input.chatId,
          callee_user_id: input.calleeUserId,
          media_kind: input.mediaKind,
          sdp: offer.sdp,
        });
      } catch (error) {
        notify(
          webRtc().isPermissionDenied(error)
            ? 'Нет доступа к микрофону или камере.'
            : 'Не удалось начать звонок.',
          'error',
        );
        teardown();
      }
    },
    [client, createPeer, store, teardown],
  );

  const accept = useCallback(
    async (offerSdp: string) => {
      const { callId, mediaKind, phase } = store.getState();
      if (!callId || !client) return;
      // Answering is one action, however many times the button is pressed:
      // the second press would build a whole second call on top of the first.
      if (phase !== 'incoming') return;
      store.getState().set({ phase: 'connecting' });
      try {
        const connection = await createPeer(callId, mediaKind);
        await connection.setRemoteDescription({ type: 'offer', sdp: offerSdp });
        for (const candidate of pendingCandidates.current) {
          await connection.addIceCandidate(candidate);
        }
        pendingCandidates.current = [];
        client.trySend('call.accept', { call_id: callId });
        const answer = await connection.createAnswer();
        await connection.setLocalDescription(answer);
        client.trySend('call.answer', { call_id: callId, sdp: answer.sdp });
      } catch {
        notify('Не удалось принять звонок.', 'error');
        client.trySend('call.reject', { call_id: callId });
        teardown();
      }
    },
    [client, createPeer, store, teardown],
  );

  const reject = useCallback(() => {
    const { callId } = store.getState();
    if (callId) client?.trySend('call.reject', { call_id: callId });
    teardown();
  }, [client, store, teardown]);

  const hangUp = useCallback(() => {
    const { callId } = store.getState();
    if (callId) client?.trySend('call.end', { call_id: callId });
    teardown();
  }, [client, store, teardown]);

  const toggleMicrophone = useCallback(() => {
    const { localStream, micEnabled } = store.getState();
    localStream?.getAudioTracks().forEach((track) => {
      track.enabled = !micEnabled;
    });
    store.getState().set({ micEnabled: !micEnabled });
  }, [store]);

  const toggleCamera = useCallback(() => {
    const { localStream, cameraEnabled } = store.getState();
    localStream?.getVideoTracks().forEach((track) => {
      track.enabled = !cameraEnabled;
    });
    store.getState().set({ cameraEnabled: !cameraEnabled });
  }, [store]);

  const offerSdp = useRef<string>('');

  useEffect(
    () =>
      subscribe((frame: ServerFrame) => {
        const payload = frame.payload as Record<string, string | number | null>;
        const callId = String(payload.call_id ?? '');

        switch (frame.type) {
          case 'call.offer': {
            if (store.getState().phase !== 'idle') {
              // Already busy: decline without disturbing the current call.
              client?.trySend('call.reject', { call_id: callId });
              return;
            }
            offerSdp.current = String(payload.sdp ?? '');
            store.getState().set({
              phase: 'incoming',
              callId,
              chatId: String(payload.chat_id ?? ''),
              peerUserId: String(payload.caller_user_id ?? ''),
              peerName: 'Входящий звонок',
              mediaKind: payload.media_kind === 'video' ? 'video' : 'audio',
            });
            return;
          }
          case 'call.answer': {
            if (store.getState().callId !== callId) return;
            void peer.current
              ?.setRemoteDescription({ type: 'answer', sdp: String(payload.sdp ?? '') })
              .then(async () => {
                for (const candidate of pendingCandidates.current) {
                  await peer.current?.addIceCandidate(candidate);
                }
                pendingCandidates.current = [];
              })
              .catch(() => notify('Не удалось установить соединение.', 'error'));
            store.getState().set({ phase: 'connecting' });
            return;
          }
          case 'call.ice_candidate': {
            if (store.getState().callId !== callId) return;
            const candidate: IceCandidateInit = {
              candidate: String(payload.candidate ?? ''),
              sdpMid: payload.sdp_mid === null ? null : String(payload.sdp_mid),
              sdpMLineIndex:
                payload.sdp_mline_index === null ? null : Number(payload.sdp_mline_index),
            };
            // Candidates can arrive before the remote description is set;
            // holding them avoids discarding a viable route.
            if (peer.current?.remoteDescription) {
              void peer.current.addIceCandidate(candidate).catch(() => undefined);
            } else {
              pendingCandidates.current.push(candidate);
            }
            return;
          }
          case 'call.taken': {
            // Sent to every device of the callee when the call is answered or
            // declined somewhere. Only a device still ringing acts on it: the
            // one that answered is already past 'incoming', and the one that
            // declined has no call left to match.
            if (store.getState().callId !== callId) return;
            if (store.getState().phase !== 'incoming') return;
            notify(
              payload.reason === 'accepted'
                ? 'Звонок принят на другом устройстве'
                : 'Звонок отклонён на другом устройстве',
            );
            teardown();
            return;
          }
          case 'call.reject':
          case 'call.end': {
            if (store.getState().callId !== callId) return;
            notify(frame.type === 'call.reject' ? 'Звонок отклонён' : 'Звонок завершён');
            teardown();
            return;
          }
          default:
            return;
        }
      }),
    [client, store, subscribe, teardown],
  );

  // Disappearing — a tab closing, an app being killed — must not leave the
  // other side ringing at nobody.
  useEffect(
    () =>
      onAppClosing(() => {
        const { callId } = store.getState();
        if (callId) client?.trySend('call.end', { call_id: callId });
      }),
    [client, store],
  );

  return {
    start,
    accept: () => accept(offerSdp.current),
    reject,
    hangUp,
    toggleMicrophone,
    toggleCamera,
  };
}
